import { capitalize, keyBy } from 'lodash-es';
import { create } from 'zustand';

import { mergeObject } from '@/package';
import { getEnvValue } from '@/package';
import {
  getColorByLabel,
  getDarkColorByLabel,
  getDimensionByLabel,
} from '@/util';

import type {
  LabelItem,
  LabelItemManagementStoreActions,
  LabelItemManagementStoreState,
} from './type';
import { createLabelItem } from './util';

const DEFAULT_LABEL_NAME_LIST = getEnvValue('VITE_APP_LABELING_LIST', value =>
  value.split(',').map(capitalize),
) as ('car' | 'truck' | 'pedestrian' | 'cyclist' | 'van' | 'bus' | 'unknown')[];

const INIT_STATE: LabelItemManagementStoreState = {
  labelItemList: DEFAULT_LABEL_NAME_LIST.map(name =>
    createLabelItem({
      color: {
        dark: getDarkColorByLabel(name).replace('0x', '#'),
        light: getColorByLabel(name).replace('0x', '#'),
      },
      dimension: getDimensionByLabel(name).reduce(
        (acc, cur, i) => {
          const key =
            i === 0 ? 'width' : i === 1 ? 'height' : i === 2 ? 'depth' : null;

          if (!key) return acc;

          return {
            ...acc,
            [key]: isNaN(cur) ? 1 : +cur,
          };
        },
        {
          width: 1,
          height: 1,
          depth: 1,
        },
      ),
      name,
      value: name,
    }),
  ),
  get enabledLabelItemList() {
    return this.labelItemList;
  },
};

const useLabelItemManagementStore = create<
  LabelItemManagementStoreState & LabelItemManagementStoreActions
>((set, get) => {
  return {
    ...INIT_STATE,

    addLabelItemList(labelItemParamList) {
      get().setLabelItemList([
        ...get().labelItemList,
        ...labelItemParamList.map(createLabelItem),
      ]);
    },

    clear() {
      set({ ...INIT_STATE });
    },

    findLabelItemById(labelItemID) {
      return get().labelItemList.find(item => item.id === labelItemID);
    },

    findLabelItemByName(labelItemName) {
      return get().labelItemList.find(item => item.name === labelItemName);
    },

    findLabelItemByValue(labelItemValue) {
      return get().labelItemList.find(item => item.value === labelItemValue);
    },

    removeItemByIdList(...labelItemIDList) {
      const labelItemIDMap = keyBy(labelItemIDList) as Dictionary<string>;
      const newLabelItemList = get().labelItemList.filter(
        item => !labelItemIDMap[item.id],
      );

      get().setLabelItemList(newLabelItemList);
    },

    setColorById(labelItemID, color) {
      get().setLabelItemById(labelItemID, { color });
    },

    setDimensionById(labelItemID, dimension) {
      get().setLabelItemById(labelItemID, { dimension });
    },

    setDisableById(labelItemID) {
      get().setLabelItemById(labelItemID, { disabled: true });
    },

    setEnableById(labelItemID) {
      get().setLabelItemById(labelItemID, { disabled: false });
    },

    setNameById(labelItemID, name) {
      get().setLabelItemById(labelItemID, { name });
    },

    setLabelItemById(labelItemID, newLabelItem) {
      const labelItem = get().findLabelItemById(labelItemID);

      if (!labelItem) {
        console.warn(
          `labelItemID: ${labelItemID} is not exists in labelItemList: ${get().labelItemList}`,
        );
        return;
      }

      get().setLabelItemList(
        get().labelItemList.map(item => {
          if (item.id !== labelItemID) return item;

          return mergeObject(labelItem, newLabelItem) as LabelItem<any>;
        }),
      );
    },

    setLabelItemByValue(labelItemValue, newLabelItem) {
      const labelItem = get().findLabelItemByValue(labelItemValue);

      if (!labelItem) {
        console.warn(
          `labelItemValue: ${labelItemValue} is not exists in labelItemList: ${get().labelItemList}`,
        );
        return;
      }

      get().setLabelItemById(labelItem.id, newLabelItem);
    },

    setLabelItemList(newLabelItemList) {
      set({
        labelItemList: newLabelItemList,
        enabledLabelItemList: newLabelItemList.filter(item => !item.disabled),
      });
    },
  };
});

export default useLabelItemManagementStore;
